// ABOUTME: Shared utility functions for developmental tracker cards age range filtering and display logic
// ABOUTME: Handles in-range and out-of-range children positioning across MeltdownTrackerCard, TriangleTrackerCard, and OneOnOneTimeTrackerCard

/**
 * Filters developmental ranges to show exactly 3 ranges, positioning the child's data appropriately.
 * Handles children within normal ranges (2-18) and out-of-range children (younger or older).
 *
 * @param {Array} developmentalRanges - All available developmental ranges from API
 * @param {number} childAge - The child's calculated age
 * @param {string} childName - The child's name for labeling
 * @param {string} periodKey - The period property name ('minPerDay', 'minPerWeek', etc.)
 * @returns {Object} { filteredRanges: Array, showDataInColumn: number (0-2) }
 */
export function filterDevelopmentalRanges(developmentalRanges, childAge, childName, periodKey = 'minPerWeek') {
  const currentAgeIndex = developmentalRanges.findIndex(range => range.isCurrentAge);
  let filteredRanges = [];
  let showDataInColumn = null; // Which column (0, 1, 2) should show the child's actual data

  const minKey = periodKey;
  const maxKey = periodKey.replace('min', 'max');

  if (currentAgeIndex >= 0) {
    if (currentAgeIndex === 0) {
      // Child is in lowest age range - show current + 2 above
      filteredRanges = developmentalRanges.slice(0, 3);
      showDataInColumn = 0;
    } else if (currentAgeIndex === developmentalRanges.length - 1) {
      // Child is in highest age range - show 2 below + current
      filteredRanges = developmentalRanges.slice(-3);
      showDataInColumn = 2;
    } else {
      // Child is in middle - show 1 below + current + 1 above
      filteredRanges = [
        developmentalRanges[currentAgeIndex - 1],
        developmentalRanges[currentAgeIndex],
        developmentalRanges[currentAgeIndex + 1]
      ];
      showDataInColumn = 1;
    }
  } else {
    // Child doesn't fit any age range - determine if younger or older
    const youngestRange = developmentalRanges[0];

    if (childAge < youngestRange.ageGroupStart) {
      // Child is younger than youngest range - show first 2 ranges + child column on left
      filteredRanges = [
        {
          ageGroupStart: childAge,
          ageGroupEnd: childAge,
          label: `${childName || 'Child'}, ${childAge}`,
          [minKey]: 0, // No developmental goal for this age
          [maxKey]: 0,
          isCurrentAge: true,
          isCustomChildRange: true // Flag to identify out-of-range custom column
        },
        ...developmentalRanges.slice(0, 2)
      ];
      showDataInColumn = 0;
    } else {
      // Child is older than oldest range - show last 2 ranges + child column on right
      filteredRanges = [
        ...developmentalRanges.slice(-2),
        {
          ageGroupStart: childAge,
          ageGroupEnd: childAge,
          label: `${childName || 'Child'}, ${childAge}`,
          [minKey]: 0, // No developmental goal for this age
          [maxKey]: 0,
          isCurrentAge: true,
          isCustomChildRange: true // Flag to identify out-of-range custom column
        }
      ];
      showDataInColumn = 2;
    }
  }

  return { filteredRanges, showDataInColumn };
}

/**
 * Generates the age label for chart display.
 * Custom child ranges (out-of-range) show "Name, Age", normal ranges show "Label\nYears Old"
 *
 * @param {Object} range - The developmental range object
 * @returns {string} The formatted age label
 */
export function getAgeLabel(range) {
  return range.isCustomChildRange ? range.label : `${range.label}\nYears Old`;
}

/**
 * Generates the text for the Age Average chip.
 * Custom child ranges show "No goal for this age", normal ranges show the goal range.
 *
 * @param {Object} currentAgeRange - The current age range object
 * @param {string} periodKey - The period property name ('minPerDay', 'minPerWeek')
 * @param {string} periodLabel - The display label ('per day', 'per week', 'days per week')
 * @param {number} decimals - Number of decimal places for formatting (0 for integers, 1 for decimals)
 * @returns {string} The formatted age range text
 */
export function getAgeRangeText(currentAgeRange, periodKey, periodLabel, decimals = 0) {
  if (!currentAgeRange) {
    return 'N/A';
  }

  if (currentAgeRange.isCustomChildRange) {
    return 'No goal for this age';
  }

  const minKey = periodKey;
  const maxKey = periodKey.replace('min', 'max');

  if (decimals > 0) {
    return `${currentAgeRange[minKey].toFixed(decimals)}-${currentAgeRange[maxKey].toFixed(decimals)} ${periodLabel}`;
  } else {
    return `${currentAgeRange[minKey]}-${currentAgeRange[maxKey]} ${periodLabel}`;
  }
}

/**
 * Extracts the first name from a full name.
 *
 * @param {string} fullName - The child's full name
 * @returns {string} The first name
 */
export function getFirstName(fullName) {
  return fullName ? fullName.split(' ')[0] : '';
}
